<template>
  <div id="asuo-layout">
    <!-- header -->
    <header-standard
      v-if="$page?.baseHeader.navItems && $page?.baseHeader.navItems.length > 0"
      :is-sticky="isPrimaryNavbarSticky"
      :display-rfi-cta="displayRfiCta"
      home-title="ASU Online"
      logo-link-url="https://www.asu.edu/"
      logo-link-target="_self"
      :rfi-anchor-id="rfiAnchorId"
      :nav-items="navItems"
      :route="useRequestURL()"
      main-content-id="content"
      @asu-search-event="handleAnalyticsEvent"
      @asu-logo-click="handleAnalyticsEvent"
      @home-click="handleAnalyticsEvent"
      @home-mobile-click="handleAnalyticsEvent"
      @nav-link-click="handleAnalyticsEvent"
      @nav-collapse-shown="handleAnalyticsEvent"
      @nav-collapse-hidden="handleAnalyticsEvent"
      @request-info-click="(evt) => handleAnalyticsEvent(evt, true)"
    >
      <template #cta-block>
        <button-apply-now
          v-if="displayApplyNow"
          class="ms-lg-space-xxxs ms-xxl-space-xs"
          text-size="small"
          :type="applyNowType"
          :link-url="applyNowUrl"
          event-region="navbar"
          event-section="main navbar"
          event-component="header-global"
          :has-size-class="true"
        ></button-apply-now>
      </template>
    </header-standard>

    <main id="content" ref="content" region="main content" tabindex="-1">
      <!-- alert-internet-explorer -->
      <!-- 
          PLACEHOLDER SWAP
          <div class="asuo-ie-alert">
          <div class="container">
            <div class="row">
              <div class="col-12">
                <alert-internet-explorer></alert-internet-explorer>
              </div>
            </div>
          </div>
        </div> -->

      <!-- a wrapper for slot is needed -->
      <slot />
      <!-- the content -->

      <!-- navbar-bottom: visible only on mobile -->
      <navbar-bottom
        v-if="displayBottomNav"
        class="d-lg-none"
        :apply-now-type="applyNowType"
        :apply-now-url="applyNowUrl"
        :rfi-anchor-id="rfiAnchorId"
        :display-apply-now="displayApplyNow"
        :display-rfi-cta="displayRfiCta"
        :display-chat-cta="displayStickyCta"
        :display-contact-button="displayContactButton"
      ></navbar-bottom>
    </main>

    <!-- asuo-footer -->
    <!-- <ClientOnly> -->
    <footer-standard
      :display-secondary-menu="displayBottomNav"
      :display-apply-now="displayApplyNow"
      :display-rfi-cta="displayRfiCta"
      :display-privacy-settings="true"
      @secondary-menu-link-click="handleAnalyticsEvent"
      @tertiary-menu-link-click="handleAnalyticsEvent"
    >
      <template #primary-section>
        <primary-footer-items />
      </template>
      <template #secondary-menu>
        <nav class="nav grid-flex-menu order-1">
          <NuxtLink
            v-for="(item, index) in secondaryNavItems"
            :key="index"
            :to="item.uri"
            :target="formatLinkTarget(item.target)"
            class="nav-link me-0 py-space-xxs py-lg-0 px-space-xs text-dark-3 menu-item fw-bold"
            rel="noopener noreferrer"
            @click="handleSecondaryMenuLinkClick(item)"
          >
            {{ item.text }}
          </NuxtLink>
        </nav>
      </template></footer-standard
    >
    <!-- </ClientOnly> -->
    <!-- cookie-banner -->
    <!-- <cookie-banner> </cookie-banner> -->
  </div>
</template>

<script setup lang="ts">
import {
  analyticsComposable,
  type AnalyticsEventObject
} from "@rds-vue-ui/analytics-gs-composable";
import { HeaderStandard } from "@rds-vue-ui/header-standard";
import type { NavItem } from "@rds-vue-ui/header-standard/HeaderStandard.vue";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";
import NavbarBottom from "~/components/nav/NavbarBottom.vue";
import PrimaryFooterItems from "~/components/nav/PrimaryFooterItems.vue";
import type { BaseHeader } from "~/server/resolvers/resolvers-types";

useHead({
  noscript: [
    {
      innerHTML: `<iframe src="https://obs.sd30591.asuonline.asu.edu/ns/b19793e55420ad2efd2a0b2330366620.html?ch=PROD%20ASU%20Online" width="0" height="0" style="display:none"></iframe>`
    }
  ]
});

onMounted(() => {
  const observer = $lozad(".rds-lazy");
  observer.observe();
});

const { $lozad } = useNuxtApp();

interface Props {
  isPrimaryNavbarSticky?: boolean;
  applyNowType?: "modal" | "link";
  applyNowUrl?: string;
  displayStickyCta?: boolean;
  displayBottomNav?: boolean;
  displayApplyNow?: boolean;
  displayRfiCta?: boolean;
  rfiAnchorId?: string;
  isHeaderSticky?: boolean;
  displayContactButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isPrimaryNavbarSticky: true,
  applyNowType: "modal",
  applyNowUrl: "#",
  displayStickyCta: true,
  displayBottomNav: true,
  displayApplyNow: true,
  displayRfiCta: true,
  rfiAnchorId: "asuo-rfi-section",
  isHeaderSticky: true,
  displayContactButton: true
});

interface DefaultLayoutQueryResponse {
  baseHeader: BaseHeader;
}

const $page = await usePageQuery<DefaultLayoutQueryResponse>(
  `#graphql
    query {
      baseHeader(id: "36c62521-a023-46a6-b15b-b668e0f56758"){
        navItems {
          isActive
          htmlLink {
            text
            uri
            target
          }
          children {
            hasBorderTop
            htmlLink {
              text
              uri
              target
            }
          }
        }
      }
    }`,
  {},
  { key: "header-nav", deep: false }
);

const secondaryNavItems = [
  {
    text: "Maps and Locations",
    uri: "https://www.asu.edu/about/locations-maps",
    target: "BLNK"
  },
  {
    text: "Jobs",
    uri: "https://www.asu.edu/asujobs",
    target: "BLNK"
  },
  {
    text: "Directory",
    uri: "https://isearch.asu.edu/asu-people/",
    target: "BLNK"
  },
  {
    text: "Contact ASU",
    uri: "https://www.asu.edu/about/contact",
    target: "BLNK"
  },
  {
    text: "My ASU",
    uri: "https://my.asu.edu/",
    target: "BLNK"
  }
];

const handleAnalyticsEvent = (evt: AnalyticsEventObject, focus?: boolean) => {
  analyticsComposable.trackEvent(evt);
  if (focus === true) {
    focusRfi();
    console.log("focus!");
  }
};

const formatLinkTarget = (target: string) => {
  if (target == "SELF") {
    return "_self";
  } else {
    return "_blank";
  }
};

const handleSecondaryMenuLinkClick = (item: (typeof secondaryNavItems)[0]) => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "footer",
    "secondary footer",
    item.text.toLowerCase(),
    "footer - innovation"
  );
};

const focusRfi = () => {
  const { rfiAnchorId } = props;
  const rfi = document.querySelector<HTMLElement>(`#${rfiAnchorId}`);
  const focusable = rfi?.querySelector<HTMLElement>(
    'a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'
  );
  focusable?.focus();
};

const navItems = computed(() => {
  return $page?.baseHeader.navItems as NavItem[];
});
</script>

<style lang="scss" scoped>
main#content:focus {
  outline: none;
}
.asuo-enter-active {
  transition: opacity 0.2s;
}
.asuo-enter {
  opacity: 0;
}
nav.grid-flex-menu {
  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: column;
  justify-items: start;
  flex-wrap: wrap;
  width: 100%;
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  :deep(.btn-size) {
    padding: 8px 16px;
    font-size: 12px;
  }
  nav.grid-flex-menu {
    display: flex;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
  }
}
/* Extra large devices (large desktops, 1200px and up) */
@media (min-width: 1400px) {
  :deep(.btn-size) {
    font-size: 14px;
  }
}
</style>
